import React, { useState } from "react";
import { Calendar, X } from "lucide-react";
import { Button } from "@chakra-ui/react";

interface DateRange {
  start: Date | null;
  end: Date | null;
}

interface DateRangePickerProps {
  onDateRangeChange?: (startDate: string | null, endDate: string | null) => void;
}

const DateRangePicker: React.FC<DateRangePickerProps> = ({ onDateRangeChange }) => {
  const [dateRange, setDateRange] = useState<DateRange>({
    start: null,
    end: null,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectingStart, setSelectingStart] = useState(true);

  const daysInMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date: Date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDate = (date: Date | null) => {
    if (!date) return "";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const formatDateForAPI = (date: Date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const isSameDay = (date1: Date | null, date2: Date | null) => {
    if (!date1 || !date2) return false;
    return date1.toDateString() === date2.toDateString();
  };

  const isInRange = (date: Date) => {
    if (!dateRange.start || !dateRange.end) return false;
    return date >= dateRange.start && date <= dateRange.end;
  };

  const handleDateClick = (day: number) => {
    const selectedDate = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day
    );

    if (selectingStart) {
      setDateRange({ start: selectedDate, end: null });
      setSelectingStart(false);
    } else {
      if (dateRange.start && selectedDate < dateRange.start) {
        setDateRange({ start: selectedDate, end: dateRange.start });
      } else {
        setDateRange({ ...dateRange, end: selectedDate });
      }
      setSelectingStart(true);
    }
  };

  const clearDates = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    setDateRange({ start: null, end: null });
    setSelectingStart(true);
    
    // Notify parent component that dates are cleared
    if (onDateRangeChange) {
      onDateRangeChange(null, null);
    }
  };

  const applyDateRange = () => {
    if (onDateRangeChange && dateRange.start && dateRange.end) {
      onDateRangeChange(
        formatDateForAPI(dateRange.start),
        formatDateForAPI(dateRange.end)
      );
    }
    setIsOpen(false);
  };

  const nextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1)
    );
  };

  const prevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1)
    );
  };

  const renderCalendar = () => {
    const days = [];
    const totalDays = daysInMonth(currentMonth);
    const firstDay = getFirstDayOfMonth(currentMonth);

    // Empty cells for days before the first day of the month
    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-10" />);
    }

    // Calendar days
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(
        currentMonth.getFullYear(),
        currentMonth.getMonth(),
        day
      );
      const isStart = isSameDay(date, dateRange.start);
      const isEnd = isSameDay(date, dateRange.end);
      const inRange = isInRange(date);

      days.push(
        <button
          key={day}
          onClick={() => handleDateClick(day)}
          className={`h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-colors
            ${isStart || isEnd ? "bg-red-600 text-white" : ""}
            ${inRange && !isStart && !isEnd ? "bg-red-100 text-blue-900" : ""}
            ${
              !isStart && !isEnd && !inRange
                ? "hover:bg-gray-100 text-black-700"
                : ""
            }
          `}
        >
          {day}
        </button>
      );
    }

    return days;
  };

  return (
    <div className="bg-white border-gray-2">
      <div className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="w-full h-8 px-1 py-3 text-md md:text-sm text-left border border-gray-300 rounded-md flex items-center justify-between hover:bg-gray-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <span className="text-gray-700">
              {dateRange.start && dateRange.end
                ? `${formatDate(dateRange.start)} - ${formatDate(dateRange.end)}`
                : selectingStart
                ? "Select start date"
                : "Select end date"}
            </span>
          </div>
          {(dateRange.start || dateRange.end) && (
            <Button
              bg="white"
              p={0}
              lineHeight={0}
              width={5}
              height={5}
              onClick={(e) => {
                e.stopPropagation();
                clearDates(e);
              }}
              _hover={{ bg: "gray.50" }}
            >
              <X className="w-4 h-4 text-gray-900" />
            </Button>
          )}
        </button>

        {isOpen && (
          <div className="absolute top-full left-0 right-0 mt-2 bg-white border border-gray-200 rounded-xl shadow-2xl p-4 z-10">
            {/* Month navigation */}
            <div className="flex items-center justify-between mb-4">
              <button
                onClick={prevMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                ←
              </button>
              <span className="font-semibold text-gray-800">
                {currentMonth.toLocaleDateString("en-US", {
                  month: "long",
                  year: "numeric",
                })}
              </span>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                →
              </button>
            </div>

            {/* Day headers */}
            <div className="grid grid-cols-7 gap-1 mb-2">
              {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                <div
                  key={day}
                  className="h-10 flex items-center justify-center text-xs font-semibold text-gray-500"
                >
                  {day}
                </div>
              ))}
            </div>

            {/* Calendar grid */}
            <div className="grid grid-cols-7 gap-1">{renderCalendar()}</div>

            {/* Action buttons */}
            <div className="mt-4 pt-4 border-t border-gray-200 flex justify-between items-center">
              <button
                onClick={() => clearDates()}
                className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Clear
              </button>
              <button
                onClick={applyDateRange}
                disabled={!dateRange.start || !dateRange.end}
                className={`px-4 py-2 text-sm rounded-lg transition-colors ${
                  dateRange.start && dateRange.end
                    ? "bg-orange-700 text-white hover:bg-red-800"
                    : "bg-gray-300 text-gray-500 cursor-not-allowed"
                }`}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DateRangePicker;